import { Injectable, ComponentFactoryResolver } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { FullTextModalComponent } from 'src/app/shared/components/modals/full-text-modal/full-text-modal.component';
import { EditAnswerModalComponent } from 'src/app/shared/components/modals/edit-answer-modal/edit-answer-modal.component';
import { ModalDirective } from 'src/app/shared/directives/modal.directive';
import { AppViews } from 'src/app/shared/helpers/enums';
import { ConfirmationModalComponent } from 'src/app/shared/components/modals/confirmation-modal/confirmation-modal.component';
import { ErrorModalComponent } from 'src/app/shared/components/modals/error-modal/error-modal.component';
import { openInBrowserDocumentFileTypes } from 'src/app/shared/helpers/variables';
import { FilePreviewModalComponent } from 'src/app/shared/components/modals/file-preview-modal/file-preview-modal.component';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  constructor(private componentFactoryResolver: ComponentFactoryResolver, private sanitizer: DomSanitizer) {}

  openModal<T = Record<string, any>>(component: any, modal: ModalDirective, payload?: T) {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
    const viewContainerRef = modal.viewContainerRef;
    const componentRef = viewContainerRef.createComponent(componentFactory);

    if (payload) {
      for (const field of Object.keys(payload)) {
        componentRef.instance[field] = payload[field];
      }
    }

    return componentRef;
  }

  editAnswer(
    title: string,
    answer: string,
    id: string,
    modal: ModalDirective,
    action: EditAnswerModalComponent['action'],
    userId: string
  ): void {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(EditAnswerModalComponent);
    const viewContainerRef = modal.viewContainerRef;
    const componentRef = viewContainerRef.createComponent(componentFactory);
    (componentRef.instance as EditAnswerModalComponent).answer = answer;
    (componentRef.instance as EditAnswerModalComponent).id = id;
    (componentRef.instance as EditAnswerModalComponent).title = title;
    (componentRef.instance as EditAnswerModalComponent).action = action;
    (componentRef.instance as EditAnswerModalComponent).userId = userId;
  }

  showConfirmationModal(
    modal: ModalDirective,
    item: string,
    property: string,
    numberOfSelectedItems: number,
    selectedItems: string[],
    variants: string[],
    view: AppViews,
    type?: string
  ): void {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ConfirmationModalComponent);
    const viewContainerRef = modal.viewContainerRef;
    const componentRef = viewContainerRef.createComponent(componentFactory);
    (componentRef.instance as ConfirmationModalComponent).item = item;
    (componentRef.instance as ConfirmationModalComponent).property = property;
    (componentRef.instance as ConfirmationModalComponent).numberOfSelectedItems = numberOfSelectedItems;
    (componentRef.instance as ConfirmationModalComponent).selectedItems = selectedItems;
    (componentRef.instance as ConfirmationModalComponent).variants = variants;
    (componentRef.instance as ConfirmationModalComponent).view = view;
    (componentRef.instance as ConfirmationModalComponent).type = type;
  }

  showErrorModal(modal: ModalDirective, text: string): void {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ErrorModalComponent);
    const viewContainerRef = modal.viewContainerRef;
    const componentRef = viewContainerRef.createComponent(componentFactory);
    (componentRef.instance as ErrorModalComponent).text = text;
  }

  showFilePreview(link: string, fileType: string, modal: ModalDirective): void {
    const safeLink = this.sanitizer.bypassSecurityTrustResourceUrl(link);
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(FilePreviewModalComponent);
    const viewContainerRef = modal.viewContainerRef;
    const componentRef = viewContainerRef.createComponent(componentFactory);
    (componentRef.instance as FilePreviewModalComponent).fileType = fileType;
    if (openInBrowserDocumentFileTypes.includes(fileType)) {
      (componentRef.instance as FilePreviewModalComponent).link = safeLink;
    } else {
      (componentRef.instance as FilePreviewModalComponent).imgSrc = safeLink;
    }
  }

  showFullText(title: string, text: string, modal: ModalDirective): void {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(FullTextModalComponent);
    const viewContainerRef = modal.viewContainerRef;
    const componentRef = viewContainerRef.createComponent(componentFactory);
    (componentRef.instance as FullTextModalComponent).text = text;
    (componentRef.instance as FullTextModalComponent).title = title;
  }
}
